import { call, select } from 'redux-saga/effects'

import { getHiddenAndVisibleGroups } from '../utils/getHiddenAndVisibleGroups'
import { PAPER_ELEMENT_TYPE } from '../../../shared/constants/drawable-types'
import { RootState } from '../../../stores'
import { isNonAreaJoistLine } from '../../../utils/project/project-helper-functions'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import { selectHiddenAIIds } from '../../slices/2D'
import {
    ActiveGeometryGroup,
    GeometryVisibilityRecord,
    selectActiveGeometryGroup,
    selectDrawableGroupsGeometries,
    selectGeometryVisibilityRecord,
} from '../../slices/geometry'
import { selectIsConflictResolutionInProgress } from '../../slices/masterSetPlan'
import { selectVisibility } from '../../slices/tools'

export function selectDrawableGroups(state: RootState) {
    return selectDrawableGroupsGeometries(state)
}

export function* handleToggleDrawableGroups2D() {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const activeGeometryGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)
    const { hiddenDrawableGroupIds } = yield call(getHiddenAndVisibleGroups)
    const isConflictResInProgress: boolean = yield select(selectIsConflictResolutionInProgress)
    const geometryVisibilityRecord: GeometryVisibilityRecord = yield select(selectGeometryVisibilityRecord)
    const { isHighlightsVisible } = yield select(selectVisibility)

    const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

    const hiddenAIIds: string[] = yield select(selectHiddenAIIds)

    const visibilityTest = (item: paper.Item): boolean => {
        if (item.data.type === PAPER_ELEMENT_TYPE.HIGHLIGHT) {
            return isHighlightsVisible
        }

        // Handle AI suggestions separately - they should respect their own visibility state
        if (item.data.aiSuggestion?.id) {
            // If there's an active geometry group and this AI item belongs to it, show it
            if (activeGeometryGroup?.id && activeGeometryGroup.id === item.data.opening_group_id) {
                return true
            }

            // Otherwise, check if it's in the hidden list
            return !hiddenAIIds.includes(item.data.aiSuggestion.id)
        }

        // Selecting a group must not reveal the base geometry behind its editable conflicts.
        const isModification = item.data.materialModificationConflict

        if (isConflictResInProgress && !isModification && !item.data.createdDuringConflictResolution) return false

        // Handle regular drawables
        if (activeGeometryGroup?.id === item.data.opening_group_id) {
            return true
        }

        if (isConflictResInProgress && isModification) {
            return !hiddenDrawableGroupIds.includes(item.data.opening_group_id)
        } else if (!isConflictResInProgress || item.data.createdDuringConflictResolution) {
            // Check individual geometry visibility record first (for newly created materials)
            // If explicitly set in the record, use that value; otherwise fall back to group visibility
            const drawableId = item.data.drawable_id
            const hasExplicitVisibility = drawableId !== undefined && geometryVisibilityRecord[drawableId] !== undefined

            if (hasExplicitVisibility) {
                return geometryVisibilityRecord[drawableId] && !isNonAreaJoistLine(item)
            }

            return !hiddenDrawableGroupIds.includes(item.data.opening_group_id) && !isNonAreaJoistLine(item)
        }

        return false
    }

    yield call(workspaceTool.toggleDrawablesWithCondition, visibilityTest)
    yield call(workspaceTool.toggleModificationsWithCondition, visibilityTest)
}
